/* Shopify Cart API adapter. Loaded only by the theme; app.js keeps the prototype store. */
window.createAuraShopifyCart = D => {
  const root = window.AURA_ROUTES?.root || '/';
  const url = path => root.replace(/\/?$/, '/') + path;
  let queue = Promise.resolve(), sequence = Date.now();
  const request = async (path, body) => {
    const res = await fetch(url(path), { credentials: 'same-origin', cache: 'no-store',
      ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}) });
    const result = await res.json();
    if (!res.ok) throw new Error(result.description || result.message || 'The cart could not be updated. Please try again.');
    return result;
  };
  const cart = {
    items: [], attributes: {}, total: 0, discounts: [], loading: true,
    variantFor(handle, mode = 'pro', color = null) {
      const p = D.byHandle(handle);
      return p?.variants?.find(v => v.mode === mode && (!p.colors?.length || !color || v.color === color)) || null;
    },
    priceOf: line => line.unitPrice,
    count() { return this.items.reduce((sum, line) => sum + line.qty, 0); },
    subtotal() { return this.total; },
    discountTotal() { return this.discounts.reduce((sum, d) => sum + d.amount, 0); },
    compareTotal() { return this.items.reduce((sum, l) => sum + Math.max(l.compare || 0, l.unitPrice) * l.qty, 0); },
    hasPro() { return this.items.some(l => l.variant === 'pro'); },
    apply(state) {
      this.attributes = state.attributes || {}; this.total = state.total_price / 100;
      // Discounts Shopify applied (the automatic bundle discount, or a code), one row per title.
      // Each line also keeps its total after them (finalTotal), which the cart shows.
      const off = new Map();
      for (const line of state.items) for (const a of line.line_level_discount_allocations || []) off.set(a.discount_application.title, (off.get(a.discount_application.title) || 0) + a.amount / 100);
      for (const a of state.cart_level_discount_applications || []) off.set(a.title, (off.get(a.title) || 0) + a.total_allocated_amount / 100);
      this.discounts = [...off].map(([title, amount]) => ({ title, amount })).filter(d => d.amount > 0);
      this.items = state.items.map((line, i) => {
        // option names as a store really has them ("Instalaltion", " Installation", "Color")
        const options = line.options_with_values || [];
        const installation = options.find(o => /instal/i.test(o.name))?.value || options[0]?.value || '';
        const variant = /diy|delivered/i.test(installation) ? 'diy' : 'pro';
        const colour = options.find(o => /colou?r/i.test(o.name))?.value || null;
        // A deleted/unpublished product can remain in a real cart. Keep its line usable.
        if (!D.byHandle(line.handle)) {
          const p = { handle: line.handle, name: line.product_title, type: line.product_type, url: line.url,
            images: [line.image || D.asset('assets/img/shop-all.webp')], price: [line.price / 100, line.price / 100], compare: 0, tint: '#EEF1F4', variants: [] };
          D.rememberCartProduct(p);
        }
        const p = D.byHandle(line.handle);
        return { key: line.key, handle: line.handle, variant, color: colour,
          qty: line.quantity, variantId: line.variant_id, unitPrice: line.original_price / 100,
          finalTotal: line.final_line_price / 100,
          lineDiscounts: (line.line_level_discount_allocations || []).map(a => ({ title: a.discount_application.title, amount: a.amount / 100 })),
          compare: p.variants?.find(v => v.id === line.variant_id)?.compare || p.compare || 0,
          properties: line.properties || {}, image: line.image, sellingPlan: line.selling_plan_allocation?.selling_plan?.id,
          position: Number(line.properties?._aura_position) || i };
      }).sort((a, b) => a.position - b.position);
      this.loading = false;
      document.dispatchEvent(new CustomEvent('cart:change', { detail: state }));
      return state;
    },
    async refresh() { return this.apply(await request('cart.js')); },
    run(work) {
      const result = queue.then(async () => {
        document.documentElement.classList.add('is-cart-busy');
        try { return await work(); }
        catch (error) {
          this.loading = false;
          window.A?.toast(window.A.esc(error.message));
          document.dispatchEvent(new CustomEvent('cart:error', { detail: error.message }));
          await this.refresh().catch(() => {});
          return false;
        } finally { document.documentElement.classList.remove('is-cart-busy'); }
      });
      queue = result.catch(() => {});
      return result;
    },
    add(handle, mode = 'pro', color = null, qty = 1, properties = {}) {
      return this.run(async () => {
        if (color && !D.byHandle(handle)?.colors?.length) { properties = { Configuration: color, ...properties }; color = null; }
        const variant = this.variantFor(handle, mode, color);
        if (!variant?.available) throw new Error('This option is currently unavailable. Please choose another option.');
        const existing = this.items.find(l => l.variantId === variant.id &&
          JSON.stringify(Object.fromEntries(Object.entries(l.properties).filter(([k]) => !k.startsWith('_')))) === JSON.stringify(properties));
        await request('cart/add.js', { items: [{ id: variant.id, quantity: Math.max(1, Number(qty) || 1),
          properties: { ...properties, _aura_position: String(existing?.position ?? ++sequence) } }] });
        await this.refresh();
        document.querySelectorAll('[data-cart-count]').forEach(b => { b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump'); });
        return true;
      });
    },
    set(key, qty) { return this.run(async () => { await request('cart/change.js', { id: key, quantity: Math.max(0, Math.min(20, Number(qty) || 0)) }); await this.refresh(); return true; }); },
    remove(key) { return this.set(key, 0); },
    clear() { return this.run(async () => { await request('cart/clear.js', {}); await this.refresh(); return true; }); },
    setVariant(key, mode) {
      return this.run(async () => {
        const line = this.items.find(l => l.key === key); if (!line || line.variant === mode) return true;
        const variant = this.variantFor(line.handle, mode, line.color);
        if (!variant?.available) throw new Error('This installation option is currently unavailable.');
        const properties = { ...line.properties, _aura_position: String(line.position) };
        const added = await request('cart/add.js', { items: [{ id: variant.id, quantity: line.qty, properties,
          ...(line.sellingPlan ? { selling_plan: line.sellingPlan } : {}) }] });
        try { await request('cart/change.js', { id: key, quantity: 0 }); }
        catch (error) {
          // Retain the original line if removal fails, and undo just this addition.
          const newLine = added.items[0];
          await request('cart/change.js', { id: newLine.key, quantity: Math.max(0, newLine.quantity - line.qty) });
          throw error;
        }
        await this.refresh(); return true;
      });
    },
    setAttributes(attributes) { return this.run(async () => { this.apply(await request('cart/update.js', { attributes })); return true; }); },
    idle() { return queue; },
  };
  cart.ready = cart.run(() => cart.refresh());
  return cart;
};
